import { PAYMENTS_ENABLED, type BookingDoc, type BookingStatus } from '@makhe/shared';

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  requested: 'Requested',
  counter_proposed: 'New time offered',
  accepted: 'Accepted',
  declined: 'Declined',
  cancelled_by_buyer: 'Cancelled',
  cancelled_by_seller: 'Cancelled',
  no_show_buyer: 'No-show',
  no_show_seller: 'No-show',
  in_progress: 'In progress',
  awaiting_confirmation: 'Confirm done',
  completed: 'Completed',
  disputed: 'Disputed',
};

type Tone = 'neutral' | 'accent' | 'good' | 'warn' | 'bad';

export const BOOKING_STATUS_TONE: Record<BookingStatus, Tone> = {
  requested: 'accent',
  counter_proposed: 'warn',
  accepted: 'good',
  declined: 'bad',
  cancelled_by_buyer: 'neutral',
  cancelled_by_seller: 'neutral',
  no_show_buyer: 'bad',
  no_show_seller: 'bad',
  in_progress: 'accent',
  awaiting_confirmation: 'warn',
  completed: 'good',
  disputed: 'warn',
};

/** Short next-step hint on booking list cards. */
export function bookingNextAction(
  booking: BookingDoc,
  side: 'buyer' | 'seller',
): string | null {
  const { status, payment } = booking;
  if (side === 'seller') {
    if (status === 'requested') return booking.urgent ? 'Urgent — respond' : 'Respond';
    if (status === 'counter_proposed') return 'Waiting on buyer';
    if (status === 'accepted' && payment.status !== 'paid' && PAYMENTS_ENABLED) {
      return 'Awaiting payment';
    }
    if (status === 'accepted') return 'Ready to start';
    if (status === 'in_progress') return "I'm done";
    if (status === 'awaiting_confirmation') return 'Waiting on buyer';
    if (status === 'disputed') return 'Open dispute';
    return null;
  }

  if (status === 'requested') return 'Waiting on creator';
  if (status === 'counter_proposed') return 'Review new time';
  if (status === 'accepted' && PAYMENTS_ENABLED && payment.status === 'pending') {
    return 'Checking payment';
  }
  if (status === 'accepted' && payment.status !== 'paid' && PAYMENTS_ENABLED) {
    return payment.paystackReference || payment.status === 'failed' ? 'Pay again' : 'Pay now';
  }
  if (status === 'accepted') return 'Confirmed';
  if (status === 'in_progress') return 'In session';
  if (status === 'awaiting_confirmation') {
    if (PAYMENTS_ENABLED && payment.status !== 'paid' && payment.timing === 'after') {
      return payment.paystackReference || payment.status === 'failed' ? 'Pay again' : 'Pay to settle';
    }
    if (PAYMENTS_ENABLED && payment.status !== 'paid') {
      return payment.paystackReference || payment.status === 'failed' ? 'Pay again' : 'Pay now';
    }
    return PAYMENTS_ENABLED ? 'Confirm done' : 'Confirm completion';
  }

  if (status === 'disputed') return 'View dispute';
  return null;
}

/** Human payment line. Never print the raw Paystack/enum status alone. */
export function paymentStatusCopy(booking: BookingDoc): { title: string; body: string } {
  if (!PAYMENTS_ENABLED) {
    return {
      title: 'Pay the creator directly',
      body: 'In-app payments are off. The price is agreed here. You pay them yourself.',
    };
  }

  const { payment, payout, status } = booking;

  if (payment.status === 'refunded' || payment.status === 'partially_refunded') {
    return { title: 'Refunded', body: 'This payment was sent back.' };
  }
  if (payment.status === 'failed') {
    return {
      title: 'Payment did not go through',
      body: 'Nothing was taken. Use Pay again.',
    };
  }
  if (payment.status === 'pending') {
    return {
      title: 'Checking payment',
      body: 'If this stays here, use Pay again. You will not be charged twice.',
    };
  }
  if (payment.status === 'paid') {
    if (payout?.status === 'released') {
      return { title: 'Paid', body: 'The creator has been paid.' };
    }
    if (payout?.status === 'queued' || status === 'completed') {
      return {
        title: 'Paid',
        body: 'You confirmed it was done. The creator payout is in the queue.',
      };
    }
    return {
      title: 'Held',
      body: 'We have your payment. It is released when you confirm the service is done.',
    };
  }
  if (payment.timing === 'after') {
    return {
      title: 'Pay after the service',
      body: 'Nothing is taken yet. You pay when the work is done.',
    };
  }
  if (payment.paystackReference) {
    return {
      title: 'Pay again',
      body: 'The last checkout did not finish. Nothing was taken.',
    };
  }
  return {
    title: 'Pay in the app',
    body: 'Nothing is taken yet. After you pay, we hold it until you confirm the work is done.',
  };
}

